import {useEffect} from "react";
import {FieldPath, useFormContext, useWatch} from "react-hook-form";
import {toast} from "react-hot-toast";

import Button from "@/components/button/Button.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {SlackAlertPreview} from "@/pages/integrations/edit/subForms/slack/SlackAlertPreview.tsx";
import {SlackMessageCustomization} from "@/pages/integrations/edit/subForms/slack/SlackMessageCustomization.tsx";
import {
  DEFAULT_SLACK_CUSTOMIZATION,
  SlackAlertType,
  SlackCustomizationState,
  SlackInput,
} from "@/pages/integrations/edit/subForms/slackInfo.ts";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

interface SlackFormProps {
  serverErrors?: ServerErrors;
}

type SlackFormValues = {id?: number; settings: SlackInput};

const CUSTOMIZATION_FIELD = "settings.message_customization" as const;

const ALERT_META = {
  down: {event: "alert_raised", label: "DOWN"},
  up: {event: "alert_cleared", label: "UP"},
} as const satisfies Record<SlackAlertType, {event: string; label: string}>;

export const SlackForm = ({serverErrors}: SlackFormProps) => {
  const {setError, setValue, getValues, control} = useFormContext<SlackFormValues>();

  const watched = useWatch({control, name: CUSTOMIZATION_FIELD});
  const customization: SlackCustomizationState = {
    down: {...DEFAULT_SLACK_CUSTOMIZATION.down, ...watched?.down},
    up: {...DEFAULT_SLACK_CUSTOMIZATION.up, ...watched?.up},
  };

  const handleCustomizationChange = (next: SlackCustomizationState) => {
    setValue(CUSTOMIZATION_FIELD, next, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FieldPath<SlackFormValues>, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  const handleSendTest = async (alertType: SlackAlertType) => {
    const {event, label} = ALERT_META[alertType];
    const channel = getValues("settings.channel") || "Slack";

    try {
      const response = await fetch(`${URL_ORIGINAL_WEBSITE}/integrations/manage/ajax/test`, {
        method: "POST",
        credentials: "include",
        mode: "cors",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({...getValues(), event}),
      });
      const result = await response.json();
      if (!result?.success) throw new Error(result?.error || "unknown error");
      toast.success(`Test ${label} message sent to ${channel}`);
    } catch (err) {
      toast.error(`Test ${label} failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const oauthStart = () => {
    const bc = new BroadcastChannel("oauth_channel");

    bc.addEventListener("message", (event) => {
      setValue("settings.webhook_url", event.data.webhookUrl);
      setValue("settings.channel", event.data.channel);
    });

    window.open(
      `${URL_ORIGINAL_WEBSITE}/integrations/oauth-authorize?target=integration_slack_oauth_redirect`,
      "integration_oauth",
      "width=760,height=800,location=no,menubar=no,resizable=no,scrollbars=no,status=no,titlebar=no,toolbar=no,noreferrer"
    );
  };

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-12 mb-3">
          <Button name="authorize" onClick={oauthStart}>
            Authorize Now
          </Button>
          <FormSubtext subText="Click 'Authorize Now' to populate the required fields below." />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField name="settings.webhook_url" label="Webhook URL" required={true} disabled={true} />
        </div>
        <div className="col-6 mb-3">
          <InputField name="settings.channel" label="Channel" required={true} disabled={true} />
        </div>
      </div>
      <SetupInfo>
        <div className="p-2">
          Click the "Authorize Now" button and login to Slack in the popup window. Then, authorize the Uptime.com app
          and select which channel to send notifications to when your checks fail or recover.
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/115002561065-Configuring-Slack">
            View {IntegrationModuleInfo[IntegrationModules.SLACK].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>

      <SlackMessageCustomization value={customization} onChange={handleCustomizationChange} />
      <SlackAlertPreview customization={customization} onSendTest={handleSendTest} />
    </>
  );
};
